// =============================================================================
// BookingModal — Types & Constants
// =============================================================================

import type { AvailableSlot, OptimizeSuggestionResponse } from '../../../api/hooks';

// Re-export for internal module use
export type { AvailableSlot, OptimizeSuggestionResponse };

// ===========================================================================
// Wizard Types
// ===========================================================================

export type BookingStep = 'customer-service' | 'smart-constraints' | 'slot-confirm';
export type BookingMode = 'catalog' | 'custom';

export interface NewCustomerForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface ResourceRequirementLocal {
  resourceTypeId: string;
  quantity: number;
}

export interface CustomEventForm {
  title: string;
  duration: number;
  color: string;
  requiresClient: boolean;
  /** Capienza clienti dell'evento custom (1 = individuale) */
  maxClients: number;
  resourceRequirements: ResourceRequirementLocal[];
}

export interface SmartConstraints {
  allowedDays: number[];
  minTime: string;
  maxTime: string;
  startFromDate: string;
  deadlineDate: string;
  /** Per-slot resource overrides: one row per unit of requirement quantity */
  overriddenRequirements: Array<{
    resourceTypeId: string;
    resourceId: string | null;
  }>;
}

export interface ResourceRequirementForPanel {
  id: string;
  resourceTypeId: string;
  quantity: number;
  resourceType?: { id: string; name: string } | null;
}

export interface ExpandedSlot {
  key: string;
  resourceTypeId: string;
  label: string;
  slotIndex: number;
}

// ===========================================================================
// Reducer State
// ===========================================================================

export interface BookingState {
  step: BookingStep;
  bookingMode: BookingMode;

  // Customer & Service
  selectedCustomerId: string;
  selectedServiceId: string;
  customerSearch: string;
  showNewCustomerForm: boolean;
  newCustomer: NewCustomerForm;

  // Custom Event
  customEvent: CustomEventForm;
  showResourcePicker: boolean;

  // Slot selection
  slotDate: Date;
  selectedAvailableSlot: AvailableSlot | null;
  showManualTimeInput: boolean;
  manualStartTime: string;

  // Eventi di gruppo (solo catalog)
  /** Override della capienza del servizio per il nuovo evento (null = default servizio) */
  maxClientsOverride: number | null;
  /** Se lo slot selezionato è sia libero che joinable: unisciti all'evento o creane uno nuovo */
  joinMode: 'join' | 'create';

  // Smart Constraints
  smartConstraints: SmartConstraints;

  // Optimization
  optimizationSuggestion: OptimizeSuggestionResponse | null;
  showOptimizationPanel: boolean;

  // Errors
  submitError: string | null;
}

// ===========================================================================
// Constants
// ===========================================================================

export const EMPTY_NEW_CUSTOMER: NewCustomerForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

export const EMPTY_CUSTOM_EVENT: CustomEventForm = {
  title: '',
  duration: 30,
  color: '#3B82F6',
  requiresClient: false,
  maxClients: 1,
  resourceRequirements: [],
};

export const EMPTY_SMART_CONSTRAINTS: SmartConstraints = {
  allowedDays: [],
  minTime: '',
  maxTime: '',
  startFromDate: '',
  deadlineDate: '',
  overriddenRequirements: [],
};

export const DAY_LABELS = [
  { value: 1, labelKey: 'daysShort.mon' },
  { value: 2, labelKey: 'daysShort.tue' },
  { value: 3, labelKey: 'daysShort.wed' },
  { value: 4, labelKey: 'daysShort.thu' },
  { value: 5, labelKey: 'daysShort.fri' },
  { value: 6, labelKey: 'daysShort.sat' },
  { value: 0, labelKey: 'daysShort.sun' },
] as const;

export const COLOR_PRESETS = [
  { value: '#3B82F6', labelKey: 'colors.blue' },
  { value: '#10B981', labelKey: 'colors.green' },
  { value: '#F59E0B', labelKey: 'colors.orange' },
  { value: '#EF4444', labelKey: 'colors.red' },
  { value: '#8B5CF6', labelKey: 'colors.purple' },
  { value: '#EC4899', labelKey: 'colors.pink' },
  { value: '#06B6D4', labelKey: 'colors.cyan' },
  { value: '#84CC16', labelKey: 'colors.lime' },
  { value: '#6B7280', labelKey: 'colors.gray' },
  { value: '#F97316', labelKey: 'colors.deepOrange' },
] as const;
